/**
 * @file base_machine_app_interface.js
 * @author MachineApps Team
 *
 * @description Base class of the two interfaces provided to the MachineApp calibrator
 * and generator:
 *
 * 1) ./edge_machine_app_interface.js
 * 2) vention_assembler/client/src/gui/machine_app/cloud_machine_app_interface.js
 *
 * This file provides common functionality that is required in both environments.
 *
 */

const environment = require('../environment');
const { showConfirmationModal } = require('./components/machine_app_confirmation_modal');
const { modalMessageBox } = require('./components/machine_app_modal_toast_message_box');
const { showMessageBox } = require('./components/machine_app_toast_message_box');
function BaseMachineAppInterface(pMachineLogicLibrary, pMachineAppLibraryMgr) {
  this.mMachineLogicLibrary         = pMachineLogicLibrary;
  this.mMachineAppLibraryManager    = pMachineAppLibraryMgr;
}

BaseMachineAppInterface.prototype.getEnvironment = function() {
  const platform = environment.getPlatform();
  if(platform === "pendant"){
    return "edge";
  }
  else if (platform === "cad"){
    return "cloud";
  }
  else {
    throw new Error(`Unknown environment: ${platform}`);
  }
};

BaseMachineAppInterface.prototype.isTouchScreen = function() {
  return window._main?.ifTouchScreen?.();
};

BaseMachineAppInterface.prototype._getMachineLogicLibrary = function() {
  return this.mMachineLogicLibrary;
};

BaseMachineAppInterface.prototype.getMachineAppLibrary = function(pType) {
  const lLibrary = this.mMachineAppLibraryManager.getLibraryByType(pType);
  if (!lLibrary) {
    console.error('Could not find MachineApp library with type=' + pType);
    return undefined;
  }

  return lLibrary;
};

BaseMachineAppInterface.prototype.listAxes = function() {
  const lMachineLogicLibrary = this._getMachineLogicLibrary();
  const lConfigManager = lMachineLogicLibrary.getConfigManager();

  return lConfigManager.listAxes();
};

BaseMachineAppInterface.prototype.getAxis = function(pUuid) {
  const lMachineLogicLibrary = this._getMachineLogicLibrary();
  const lConfigManager = lMachineLogicLibrary.getConfigManager();

  return lConfigManager.getAxisByUuid(pUuid);
};

BaseMachineAppInterface.prototype.listInputs = function() {
  const lMachineLogicLibrary = this._getMachineLogicLibrary();
  const lConfigManager = lMachineLogicLibrary.getConfigManager();

  return lConfigManager.listInputs();
};

BaseMachineAppInterface.prototype.getInput = function(pUuid) {
  const lMachineLogicLibrary = this._getMachineLogicLibrary();
  const lConfigManager = lMachineLogicLibrary.getConfigManager();

  return lConfigManager.getInputByUuid(pUuid);
};

BaseMachineAppInterface.prototype.listOutputs = function() {
  const lMachineLogicLibrary = this._getMachineLogicLibrary();
  const lConfigManager = lMachineLogicLibrary.getConfigManager();

  return lConfigManager.listOutputs();
};

BaseMachineAppInterface.prototype.getOutput = function(pUuid) {
  const lMachineLogicLibrary = this._getMachineLogicLibrary();
  const lConfigManager = lMachineLogicLibrary.getConfigManager();

  return lConfigManager.getOutputByUuid(pUuid);
};

BaseMachineAppInterface.prototype.listControllers = function() {
  const lMachineLogicLibrary = this._getMachineLogicLibrary();
  const lConfigManager = lMachineLogicLibrary.getConfigManager();
  return lConfigManager.getConfigurationList().map(function(pConfig) { return pConfig.getController(); });
};

BaseMachineAppInterface.prototype.getControllerName = function(pUuid) {
  const lMachineLogicLibrary = this._getMachineLogicLibrary();
  const lConfigManager = lMachineLogicLibrary.getConfigManager();
  const controllerName = lConfigManager.getConfiguration(pUuid).getController().getName();
  return controllerName;
};

BaseMachineAppInterface.prototype.listRobots = function() {
  return this._getMachineLogicLibrary()
    .getConfigManager()
    .listRobots();
};

BaseMachineAppInterface.prototype.getRobot = function(pUuid) {
  const lRobot = this.listRobots().find(pRobot => pRobot.getRobotId() === pUuid);

  if (!lRobot) {
    console.warn("Couldn't find the robot specified in the Machine Parameters. It might have been removed from configuration.");
  }

  return lRobot;
};

/*
  * MACHINEAPP COMPONENTS
  */
BaseMachineAppInterface.prototype.showMessageBox = function(pMessage, pLevel, pOptions) {
  if(pOptions?.showOutsideOfModal){
    showMessageBox(pMessage, pLevel, pOptions);
  }
  else{
    modalMessageBox(pMessage, pLevel, pOptions);
  }
};

BaseMachineAppInterface.prototype.showConfirmationModal = function(pOptions) {
  return showConfirmationModal({...pOptions});
};

/*******************************************************************************
  * EDGE AND CLOUD NOT IMPLEMENTED IN THIS CONTEXT
  ********************************************************************************/
BaseMachineAppInterface.prototype.isProdDeployment = function() {
  throw new Error('isProdDeployment is an abstract method and must be implemented.');
};

BaseMachineAppInterface.prototype.startRobotJogger = function(pRobotId, { onStop, onTeach, closeButtonLabel }) {
  throw new Error('startRobotJogger is an abstract method and must be implemented.');
};

BaseMachineAppInterface.prototype.getRobotPosition = async function() {
  throw new Error('getJoggerPosition is an abstract method and must be implemented.');
};

BaseMachineAppInterface.prototype.getRobotReferenceFrameWorld = function(pRobotActuatorVID) {
  throw new Error('getRobotReferenceFrameWorld is an abstract method and must be implemented.');
};

BaseMachineAppInterface.prototype.cleanupCalibratorManipulators = function() {
  throw new Error('cleanupCalibratorManipulators is an abstract method and must be implemented.');
};

BaseMachineAppInterface.prototype.graphHasAxis = function(pVid) {
  throw new Error('graphHasAxis is an abstract method and must be implemented.');
};

BaseMachineAppInterface.prototype.getCartesianJogger = function({ axisMapping, options, onPosition, instructionText }) {
  throw new Error('getCartesianJogger is an abstract method and must be implemented.');
};

/*
  * Update / Redirect views NOT USED IN MACHINE-APP-COMPONENTS
  */
BaseMachineAppInterface.prototype.updateMachineAppView = function() {
  throw new Error('updateMachineAppView is an abstract method and must be implemented.');
};

BaseMachineAppInterface.prototype.redirectMachineAppToConfigure = function() {
  throw new Error('redirectMachineAppToConfigure is an abstract method and must be implemented.');
};

BaseMachineAppInterface.prototype.redirectMachineAppToMachineLogic = function() {
  throw new Error('redirectMachineAppToMachineLogic is an abstract method and must be implemented.');
};

BaseMachineAppInterface.prototype.redirectMachineAppToAppLauncher = function(pAppName) {
  throw new Error('redirectMachineAppToAppLauncher is an abstract method and must be implemented.');
};

BaseMachineAppInterface.prototype.redirectToMachineAppView = function(pAppType, pAppView) {
  throw new Error('redirectToMachineAppView is an abstract method and must be implemented.');
};

module.exports = {
  BaseMachineAppInterface: BaseMachineAppInterface
};
